import React, { useEffect, useState } from 'react';
import { CheckCircle, Plus, Loader } from 'lucide-react';
import { Button } from '@/components/ui/button';
import InstanceCard from './InstanceCard';
import useInstanceStore from '@/stores/instanceStore';
import { toast } from 'sonner';
import { getAuthHeader } from '@/services/authToken';
import useUploadLimit from '@/hooks/useUploadLimit';
import UploadLimitModal from './pdf/UploadLimitModal';

const InstanceGrid = ({
  onNewInstance,
  onInstanceSelect,
  instancesOverride,
  readOnly = false,
  emptyState,
  onRetry
}) => {
  const {
    instances,
    selectedInstanceId,
    loading,
    error,
    setInstances,
    setLoading,
    setError
  } = useInstanceStore();

  const [localLoading, setLocalLoading] = useState(true);

  // Upload limit check
  const { checkLimit, limitStatus, loading: limitLoading, resetStatus } = useUploadLimit();
  const [showLimitModal, setShowLimitModal] = useState(false);

  // Handle New button click - check limit first
  const handleNewClick = async () => {
    // Check upload limit before proceeding
    const status = await checkLimit();
    setShowLimitModal(true);
  };

  // Handle proceed from limit modal
  const handleProceedToUpload = () => {
    setShowLimitModal(false);
    resetStatus();
    // Call the original onNewInstance to open upload modal
    if (onNewInstance) {
      onNewInstance();
    }
  };

  // Handle close limit modal
  const handleCloseLimitModal = () => {
    setShowLimitModal(false);
    resetStatus();
  };

  // Use override instances if provided, otherwise use store
  const displayInstances = instancesOverride || instances;
  const isOverrideMode = !!instancesOverride;

  // Load instances on mount (skip if override mode)
  useEffect(() => {
    if (!isOverrideMode) {
      loadInstances();
    } else {
      setLocalLoading(false); // Don't show loading if using override
    }

    // Listen for instance progress updates (only in normal mode)
    if (!isOverrideMode) {
      const handleInstanceProgressUpdate = (event) => {
        const { instanceId, stage, progress } = event.detail;
        console.log(`🔄 InstanceGrid received progress update:`, { instanceId, stage, progress });

        // Update the specific instance in the store
        const status = stage === 'completed' ? 'ready' :
                     stage === 'failed' ? 'failed' : 'processing';

        handleInstanceUpdate(instanceId, {
          status,
          progress: Math.round(progress || 0),
          processing_stage: stage
        });
      };

      window.addEventListener('instance-progress-update', handleInstanceProgressUpdate);

      return () => {
        window.removeEventListener('instance-progress-update', handleInstanceProgressUpdate);
      };
    }
  }, [isOverrideMode]);

  const loadInstances = async () => {
    try {
      setLoading(true);
      setLocalLoading(true);
      
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8002';
      
      // Add timeout to prevent infinite loading
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
      
      const response = await fetch(`${backendUrl}/api/instances`, {
        headers: getAuthHeader(),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ InstanceGrid: Loaded instances:', data.instances?.length || 0);
        
        // Transform instances to include required fields
        const transformedInstances = (data.instances || []).map(instance => ({
          id: instance.id,
          name: instance.name || t('profile.untitled_item'),
          description: instance.description || t('profile.no_description'),
          thumbnail: instance.cover_image || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=200&fit=crop',
          cover_image: instance.cover_image || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&h=400&fit=crop',
          status: instance.status || 'ready',
          progress: instance.progress || 100,
          posts_count: instance.posts_count || 0,
          created_at: instance.created_at,
          updated_at: instance.updated_at,
          owner_id: instance.owner_id || instance.user_id
        }));

        setInstances(transformedInstances);
        setError(null);
      } else if (response.status === 404 || response.status === 204) {
        // No instances found - not an error, just empty state
        console.log('ℹ️ No instances found (404/204), showing empty state');
        setInstances([]);
        setError(null);
      } else {
        throw new Error(`Failed to fetch instances: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error loading instances:', error);
      
      // Check if it was an abort error (timeout)
      if (error.name === 'AbortError') {
        console.log('⚠️ Request timed out, showing empty state');
        setInstances([]); // Show empty state instead of error
        setError(null);
      } else {
        // For other errors, show empty state with option to create
        console.log('⚠️ Failed to load instances, showing empty state');
        setInstances([]);
        setError(null); // Don't show error, just show empty state
      }
    } finally {
      setLoading(false);
      setLocalLoading(false);
    }
  };

  const handleInstanceClick = async (instance) => {
    try {
      console.log('InstanceGrid: Instance clicked:', instance.name);

      if (readOnly) {
        if (onInstanceSelect) {
          onInstanceSelect(instance);
        }
        return;
      }
      
      // Don't allow selection if it's already selected
      if (selectedInstanceId === instance.id) {
        console.log('InstanceGrid: Instance already selected');
        return;
      }

      // Call backend to persist selection
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8002';
      
      const response = await fetch(`${backendUrl}/api/instances/${instance.id}/activate`, {
        method: 'POST',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });

      if (response.ok) {
        console.log('InstanceGrid: Instance selection persisted to backend');
      } else {
        console.warn('InstanceGrid: Failed to persist selection to backend, continuing locally');
      }

      // Update Zustand store
      useInstanceStore.getState().setSelectedInstanceId(instance.id);
      
      // Notify parent component
      if (onInstanceSelect) {
        onInstanceSelect(instance);
      }
      
      toast.success(`${t('profile.item_selected', { name: instance.name })}`);
    } catch (error) {
      console.error('Error selecting instance:', error);
      toast.error(t('profile.item_selection_failed'));
    }
  };
  const handleInstanceUpdate = (instanceId, updates) => {
    if (readOnly) return;
    useInstanceStore.getState().updateInstance(instanceId, updates);
    // Reload instances to get fresh data
    loadInstances();
  };
  const handleInstanceDelete = (instanceId) => {
    if (readOnly) return;
    useInstanceStore.getState().removeInstance(instanceId);
    toast.success(t('profile.item_deleted_success'));
  };
  // Don't show loading skeleton in override mode (parent handles it)
  if (localLoading && !isOverrideMode) {
    return (
      <div className="p-2 sm:p-4">
        <div className="grid grid-cols-3 gap-1 sm:gap-2">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              data-testid="loading-skeleton"
              className="aspect-square bg-gray-200 rounded-lg animate-pulse"
            />
          ))}
        </div>
      </div>
    );
  }

  if (error && !isOverrideMode) {
    return (
      <div className="p-6 text-center">
        <div className="text-red-600 mb-4">
          <p className="font-medium">t('profile.library_load_failed')</p>
          <p className="text-sm text-gray-600">{error}</p>
        </div>
        <Button
          variant="outline"
          onClick={onRetry || loadInstances}
          size="sm"
        >
          Try Again
        </Button>
      </div>
    );
  }

  if (displayInstances.length === 0) {
    // Use custom empty state if provided
    if (emptyState) {
      return emptyState;
    }

    // Default empty state (only for normal mode)
    if (!isOverrideMode) {
      return (
        <div className="p-6 text-center">
          <div className="text-gray-500 mb-6">
            <p className="font-medium text-lg">No library items yet</p>
            <p className="text-sm">Upload your first PDF to get started</p>
          </div>
          <Button
            onClick={handleNewClick}
            disabled={limitLoading}
            className="flex items-center gap-2"
          >
            {limitLoading ? (
              <Loader className="w-4 h-4 animate-spin" />
            ) : (
              <Plus className="w-4 h-4" />
            )}
            Create First Library Item
          </Button>
        </div>
      );
    }

    return null; // Override mode with no items - parent should handle
  }

  return (
    <div className="p-2 sm:p-4">
      {/* Only show header with New button in normal mode */}
      {!readOnly && (
        <div className="flex items-center justify-between mb-3 sm:mb-4">
          <h3 className="font-semibold text-gray-900">My Library</h3>
          <Button size="sm" onClick={handleNewClick} disabled={limitLoading}>
            {limitLoading ? (
              <Loader className="w-4 h-4 mr-1 animate-spin" />
            ) : (
              <Plus className="w-4 h-4 mr-1" />
            )}
            New
          </Button>
        </div>
      )}

      <div className="grid grid-cols-3 gap-1 sm:gap-2">
        {displayInstances.map((instance) => (
          <InstanceCard
            key={instance.id}
            instance={instance}
            isSelected={selectedInstanceId === instance.id}
            onClick={() => handleInstanceClick(instance)}
            onUpdate={readOnly ? undefined : handleInstanceUpdate}
            onDelete={readOnly ? undefined : handleInstanceDelete}
            readOnly={readOnly}
          />
        ))}
      </div>

      {!readOnly && selectedInstanceId && (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-2 text-blue-700">
            <CheckCircle className="w-4 h-4" />
            <span className="text-sm font-medium">
              {displayInstances.find(i => i.id === selectedInstanceId)?.name} is your active library item
            </span>
          </div>
        </div>
      )}

      {/* Upload Limit Modal - shown before allowing upload */}
      <UploadLimitModal
        isOpen={showLimitModal}
        onClose={handleCloseLimitModal}
        onProceed={handleProceedToUpload}
        limitStatus={limitStatus}
      />
    </div>
  );
};

export default InstanceGrid;



